import {
    stringify as qsify,
} from 'query-string';

import appSettings from 'wanderu-config';

import ApiService from './apiService/apiService';

const GET_DEFAULT_OPTIONS = {
    withAuth: true,
};

const POST_DEFAULT_OPTIONS = {
    withAuth: true,
    authViaHeader: false,
    contentType: 'text/plain',
};

function buildUrl(path) {
    return appSettings.config.wapi.urlRoot + path;
}

export async function get(path, init) {
    return ApiService.get(buildUrl(path), init, { ...GET_DEFAULT_OPTIONS });
}

export async function getAggregatePricesForRoute(opts) {
    const {
        route,
        fromTime,
        toTime,
    } = opts;
    const qs = qsify({
        c2c_id: route,
        depart_datetime_min: fromTime,
        depart_datetime_max: toTime,
    });

    return get(`aggregate/pricing.json?${qs}`);
}

export function getCarrierInfo(carrierCode) {
    return ApiService.get(buildUrl(`carriers.json/${carrierCode.toUpperCase()}`))
        .then((response) => {
            return response.result;
        });
}

export async function getInterlinkC2CData(opts = {}) {
    const {
        fromCity,
        fromRegion,
        locale,
        mode,
        toCity,
        toRegion,
    } = opts;

    return getWithoutAuth(`links/c2c?trans_mode=${mode}&depart_region=${fromRegion}&depart_city=${fromCity}&arrive_region=${toRegion}&arrive_city=${toCity}&locale=${locale}`);
}

export async function getInterlinkHomepageData(opts = {}) {
    const {
        locale,
    } = opts;

    if (!locale) {
        throw new Error('locale must be provided');
    }

    return getWithoutAuth(`links/home?locale=${locale}`);
}

export async function getSplitTestConfiguration(requestParams) {
    const qs = {
        id: requestParams.id,
        ipAddress: requestParams.ip,
        path: requestParams.path,
        queryParams: qsify(requestParams.queryParams),
    };

    return get(`splittest.json?${qsify(qs)}`);
}

async function startNewSession(authCredentials, options) {
    return ApiService.startNewSession(authCredentials, options);
}

function getSessionId() {
    return ApiService.getSessionId();
}

function onSessionChange(listener) {
    return ApiService.onSessionChange(listener);
}

function onReauthenticate(callback) {
    return ApiService.onReauthenticate(callback);
}

export async function getStationDetails(stationId) {
    return get(`stations.json/${encodeURIComponent(stationId)}`);
}

function clearCredentials() {
    ApiService.clearCredentials();
}

export async function getSupportedCurrencies() { // this should be moved to a currency service
    return get('currency/supported.json');
}

export async function getTranslations(requestParams) {
    const qs = {
        locale: requestParams.locale,
        page_type: requestParams.pageType,
    };

    return get(`pagetranslation.json?${qsify(qs)}`);
}

export async function getTrip(tripId) {
    if (!tripId) {
        throw new Error('tripId must be provided');
    }

    return get(`trips.json/${encodeURIComponent(tripId)}`);
}

export async function getTripInformation(opts) {
    const {
        locale = '',
        tripIds = [],
        selectedFares = {},
    } = opts;
    const [
        language,
        location,
    ] = locale.split('-');

    if (!tripIds.length) {
        throw new Error('at least one tripId must be provided');
    } else if (!locale || !language || !location) {
        throw new Error('locale must be a <language>-<country> pair');
    }

    const body = {
        trip_ids: tripIds,
        locale,
        selected_fares: selectedFares,
    };

    return post('tripinfo.json', {
        body,
    });
}

export async function getTripItinerary(tripId) {
    return post('itineraryinfo.json', {
        body: JSON.stringify({
            trip_ids: [tripId],
        }),
    });
}

export async function getWithoutAuth(path, init) {
    // MAIN-6847 could the issue be that the init is not being passed down to the ServerApiService with the proper CORS headers?
    return ApiService.get(buildUrl(path), init, { ...GET_DEFAULT_OPTIONS, withAuth: false });
}

export function initService({ affiliateId, visitorId }) {
    return ApiService.initService({ affiliateId, visitorId });
}

export function injectStore(store) {
    return ApiService.injectStore(store);
}

export async function post(path, init) {
    return ApiService.post(buildUrl(path), init, { ...POST_DEFAULT_OPTIONS });
}

export async function postWithoutAuth(path, init, preserveSession = false) {
    return ApiService.post(
        buildUrl(path),
        init,
        {
            ...POST_DEFAULT_OPTIONS,
            withAuth: false,
            preserveSession,
        },
    );
}

export async function restoreSession(opts) {
    ApiService.restoreSession(opts);
}

export default {
    buildUrl,
    clearCredentials,
    get,
    getAggregatePricesForRoute,
    getCarrierInfo,
    getInterlinkC2CData,
    getInterlinkHomepageData,
    getTranslations,
    getSessionId,
    getSplitTestConfiguration,
    getStationDetails,
    getSupportedCurrencies,
    getTrip,
    getTripItinerary,
    getTripInformation,
    getWithoutAuth,
    onReauthenticate,
    onSessionChange,
    post,
    postWithoutAuth,
    startNewSession,
    restoreSession,
};
